import axios from 'axios';
import { call, all, put, takeLatest, select } from 'redux-saga/effects';

import * as focusSupplierActions from './focus-supplier-actions';
import focusSupplierService from './focus-supplier-service';

import {
  DELETE_ITEM,
  EDIT_ITEM,
  GET_BY_ID_REQUEST,
  GET_LIST_FOCUS_SUPPLIER_REQUEST,
  SAVE_FOCUS_SUPPLIER_REQUEST
} from './focus-supplier-constants';

import { ISA_ERROR } from '~/constants';

import { AppStateType } from '../root-reducer';

import { formatMessage } from '~/i18n/translate';

import history from '~/services/history';
import notification from '~/services/notification';
import domainService from '~/services/modules/domain/domain-service';
import { sort } from '../order/order-utils';

let cancel: any;
const CancelToken = axios.CancelToken;

const INITIAL_STATE = {
  providers: []
};

export function* getAllFocusSupplierList() {
  if (cancel !== undefined) cancel();
  const cancelToken = new CancelToken(function executor(c) {
    cancel = c;
  });

  try {
    yield put(focusSupplierActions.setLoadingData(true));

    const { rowsPerPage, page, order, orderBy } = yield select((state: AppStateType) => state.focusSupplier.list);

    const pagination = {
      skip: page === 0 ? 0 : page * rowsPerPage,
      limit: rowsPerPage,
      order,
      orderBy
    };

    const [{ data: bonificationProducts }, { data: count }] = yield all([
      call(
        focusSupplierService.getFocusSupplierList,
        {
          ...pagination
        },
        {
          cancelToken
        }
      ),
      call(focusSupplierService.getListCount, { ...pagination }, { cancelToken })
    ]);

    yield put(
      focusSupplierActions.getAllFocusSupplierSuccess({
        rows: bonificationProducts,
        rowsCount: count?.count
      })
    );
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(focusSupplierActions.setLoadingData(false));
  }
}

export function* getById({ id }: any) {
  try {
    yield put(focusSupplierActions.setLoadingData(true));

    const { data: providers } = yield call(domainService.getProviders);

    yield put(
      focusSupplierActions.getSelectsSuccess({
        providers: sort(providers)
      })
    );

    if (id) {
      const { data: focus } = yield call(focusSupplierService.getByIdFocusSupplier, id);

      const parsedFocus = focus[0]?.matchedProviders;

      yield put(focusSupplierActions.getByIdSuccess(parsedFocus));
    } else {
      yield put(focusSupplierActions.getByIdSuccess(INITIAL_STATE));
    }
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(focusSupplierActions.setLoadingData(false));
  }
}

export function* saveFocusSupplier({ payload, id }: any) {
  try {
    yield put(focusSupplierActions.setLoadingSend(true));

    const parsedPayload = {
      providers: payload?.providers?.map(provider => provider.externalCode)
    };

    if (id) {
      yield call(focusSupplierService.editFocusSupplier, {
        id,
        data: {
          ...parsedPayload
        }
      });
    } else {
      yield call(focusSupplierService.saveFocusSupplier, {
        ...parsedPayload
      });
    }

    yield call(
      notification.success,
      id ? formatMessage('focusSupplierUpdatedSuccess') : formatMessage('focusSupplierCreatedSuccess')
    );

    yield call(history.push, `/register/focus-supplier`);
  } catch (error) {
    if ((error as { response?: { data?: { type?: string } } })?.response?.data?.type === 'providerAlreadyExists') {
      yield call(notification.error, formatMessage('focusSupplierProviderAlreadyExists'));
    } else {
      yield put({ type: ISA_ERROR, error });
    }
  } finally {
    yield put(focusSupplierActions.setLoadingSend(false));
  }
}

export function* editItem({ id }: any) {
  yield call(history.push, `/register/focus-supplier/edit/${id}`);
}

export function* deleteItem({ id }: any) {
  try {
    const { isConfirmed } = yield call(
      notification.confirm,
      formatMessage('commonLabelExclude'),
      formatMessage('focusSupplierExcludeDeleteMessage')
    );
    if (isConfirmed) {
      yield put(focusSupplierActions.setLoadingSend(true));
      yield call(focusSupplierService.deleteFocusSupplier, id);
      yield put(focusSupplierActions.setLoadingSend(false));

      yield put(focusSupplierActions.getAllFocusSupplierRequest());
      yield call(notification.success, formatMessage('focusSupplierExcludeSuccess'));
    }
  } catch (error) {
    yield put(focusSupplierActions.setLoadingSend(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export default all([
  takeLatest(GET_LIST_FOCUS_SUPPLIER_REQUEST, getAllFocusSupplierList),
  takeLatest(GET_BY_ID_REQUEST, getById),
  takeLatest(SAVE_FOCUS_SUPPLIER_REQUEST, saveFocusSupplier),
  takeLatest(EDIT_ITEM, editItem),
  takeLatest(DELETE_ITEM, deleteItem)
]);
